// src/components/CompetitorList.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Alert,
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RowActionsMenu from './common/RowActionsMenu';
import Pill from './common/Pill';
import EmptyState from './common/EmptyState';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import CompetitorModal from './CompetitorModal';
import CompetitorListSkeleton from '../Skeletons/CompetitorListSkeleton';
import PageContainer from './common/PageContainer';
import ViewHeader from './ViewHeader';
import TableFooterSimple from './common/TableFooterSimple';
import KpiRow, { KpiCell } from './common/KpiRow';
import StatTile from './common/StatTile';
import Panel from './common/Panel';
import ListToolbar from './common/ListToolbar';
import MobileRecordCard, { KARTE_TITEL_BASIS_SX } from './common/MobileRecordCard';
import useListQuery from './common/listQuery/useListQuery';
import { formatNumber, formatQty } from '../utils/format';
import { SP } from '../theme/tokens';

// API base URL
const API = process.env.REACT_APP_API_BASE_URL || '';
const ROWS_PER_PAGE = 50;

/**
 * 🔴 G9b (UI2 Teil 10, 2026-10-20) — EINE Tabelle je Ansicht.
 *
 * Kopfzellen, die Werte der Sortier-Achse in der URL und die Optionen des
 * mobilen Sortier-Blattes kommen aus DIESER Liste (Plan §2.1a). Vorher trug
 * nur der Kopf eine eigene Liste, und das Blatt haette eine zweite daneben
 * gebraucht — zwei Quellen fuer dieselbe Ordnung.
 *
 * `width` ist die ERKLAERTE Spaltenbreite: `tableLayout: 'fixed'` nimmt sie aus
 * der ersten Zeile (dem Kopf). Ohne sie rechnet der Browser die Breiten aus
 * ALLEN Zeilen; im Leerzustand fehlen die Datenzeilen, und P33 misst einen
 * Versatz zwischen Kopf im Grund- und im Leerzustand.
 */
const SORT_FELDER = [
  { id: 'name', label: 'Competitor', numeric: false, sortLabel: 'Competitor', text: true, width: '34%' },
  { id: 'scraperCount', label: 'Scraper targets', numeric: true, width: '20%' },
  { id: 'productCount', label: 'Scraped products', numeric: true, width: '20%' },
  { id: 'marketCount', label: 'Markets', numeric: true, width: '14%' },
  { id: 'actions', label: 'Actions', numeric: false, sortierbar: false, width: '12%' },
];

/** Die URL-Werte der Sortier-Achse: `feld` (aufsteigend) und `-feld` (absteigend). */
const SORT_VALUES = SORT_FELDER
  .filter((f) => f.sortierbar !== false)
  .flatMap((f) => [f.id, `-${f.id}`]);

/** Die Radiowerte des mobilen Blattes — beide Richtungen, aus derselben Liste. */
const SORT_OPTIONS = SORT_FELDER
  .filter((f) => f.sortierbar !== false)
  .flatMap((f) => [
    { value: f.id, label: `${f.sortLabel || f.label} (${f.text ? 'A–Z' : 'ascending'})` },
    { value: `-${f.id}`, label: `${f.sortLabel || f.label} (${f.text ? 'Z–A' : 'descending'})` },
  ]);

/**
 * Der BODEN-TRAEGER des Kartentitels: ein NAME, kein Code.
 *
 * 🔴 `position: 'relative'` und der 44-px-`::after`-Boden kommen unveraendert aus
 * `KARTE_TITEL_BASIS_SX` (EINE Quelle statt Kopien). **Hier steht KEIN
 * `overflow`.**
 *
 * 🔴 R16 B1 (Nachbesserung 4, 2026-10-22): die Zwei-Zeilen-Klemme sitzt am
 * INNEREN `<span>` (`TITEL_KLEMME_SX`), nicht hier. Ein absolut positionierter
 * `::after` wird vom `overflow: hidden` seines Containing Blocks auf dessen
 * Padding-Box beschnitten (CSS 2.1 §11.1.1) — lag die Klemme am `Link`, war er
 * zugleich Containing Block UND Traeger des `overflow`, und der 44-px-Boden
 * schrumpfte auf die Textzeile (21 px einzeilig). Im Repo gemessen und behoben
 * an `ProductsTable.jsx` (R16 B1, 2026-10-15); hier stand bis zur
 * Nachbesserung 4 das Gegenteil („der Boden bleibt stehen, die Klemme fasst ihn
 * nicht") — der Satz dachte den `-webkit-box`-Fluss zu Ende und den `overflow`
 * nicht.
 *
 * `overflowWrap: 'anywhere'` ist eine VERERBTE Eigenschaft: sie erreicht den
 * Klemmen-`<span>` von hier aus, ohne zweite Kopie. `nowrap` waere falsch (ein
 * Wettbewerbername ist laenger als eine Kennung).
 */
const KARTE_TITEL_SX = {
  ...KARTE_TITEL_BASIS_SX,
  color: 'inherit',
  overflowWrap: 'anywhere',
};

/**
 * TITEL_KLEMME_SX — die Zwei-Zeilen-Klemme, am INNEREN `<span>`.
 *
 * Bauart `ProductsTable.TITEL_KLEMME_SX` (Hausform): `display: -webkit-box`,
 * `-webkit-line-clamp: 2`, `overflow: hidden`. Der `<span>` traegt keinen Boden
 * und darf deshalb beschneiden.
 *
 * Gemessen wird beides in P15 (`wettbewerber.spec.ts`): Klemme und Zeilenzahl am
 * Span (Elementhoehe / Zeilenhoehe), der Boden am `Link` — und seine WIRKUNG
 * ueber `document.elementFromPoint`, mit `overflow: hidden` als Kalibrierung.
 */
const TITEL_KLEMME_SX = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

/** Unbekannt ist nicht 0 (Regel 22 Nr. 3): `formatQty` traegt `—` als Platzhalter. */
const zahl = (wert) => formatQty(wert);


// Sorting helpers
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilized = array.map((el, idx) => [el, idx]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    if (cmp !== 0) return cmp;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

export default function CompetitorList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [competitors, setCompetitors] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // 🔴 N10.1 (G9b): Suche, Sortierung und Seite leben in der URL — genau EIN
  // Schreiber. Vorher lagen sie in vier `useState`, und ein geteilter Link
  // zeigte nie, was der Absender sah.
  const query = useListQuery({
    dimensions: [{ key: 'q', kind: 'single', label: 'Search' }],
    sort: { default: 'name', values: SORT_VALUES },
    pageSize: ROWS_PER_PAGE,
  });
  const { state, set, clearAll } = query;

  // Die Sortier-Achse traegt `feld` / `-feld` (N10.2); die Tabelle liest daraus
  // Feld und Richtung — dieselbe Umrechnung wie `VendorsOverview`.
  const sortFeld = state.sort ?? 'name';
  const sortBy = sortFeld.startsWith('-') ? sortFeld.slice(1) : sortFeld;
  const sortOrder = sortFeld.startsWith('-') ? 'desc' : 'asc';
  // `state.page` ist 1-basiert (N10.4), MUI's Seitenwechsel 0-basiert.
  const seite = state.page - 1;
  const suchbegriff = state.q ?? '';

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [competitorToDelete, setCompetitorToDelete] = useState(null);

  const handleCreateSuccess = (newComp) => {
    setModalOpen(false);
    // 🔴 Nachbesserung 5 (R16-W2): das Anlegen wird bestaetigt. Ohne die
    // Bestaetigung ist der Pfad „POST gelingt, GET scheitert" von aussen nicht
    // von „nichts passiert" zu unterscheiden — das Fenster geht zu, sonst bleibt
    // alles gleich —, und ein zweiter Versuch legt einen zweiten Wettbewerber
    // an. Doppelte sind in dieser Liste unsichtbar.
    toast.success('Competitor created.');
    // 🔴 Nachbesserung 5 (R16-B2): die Bedingung deckt jetzt BEIDE unbekannten
    // Zustaende — Fehler UND Laden. Kam der Aufruf aus dem Ladezustand, war
    // `[newComp]` als Wahrheit „Competitors: 1", und die uebrigen waeren
    // unbekannt, nicht null (Regel 22 §3). `setCompetitors(null)` ist entfallen:
    // in beiden Zweigen ist die Liste schon null.
    if (loadError || competitors === null) {
      load();
      return;
    }
    setCompetitors((prev) => {
      const base = prev || [];
      return [...base, { marketCount: 0, ...newComp }];
    });
  };

  const navigate = useNavigate();

  // Fetch competitors on mount. A failure MUST NOT leave `competitors` at null:
  // the skeleton is gated on exactly that value, so a dropped request used to
  // render the loading skeleton forever — a page that never arrives.
  // 🔴 G9b (§2.2 Punkt 8): der Fehler wird als EIGENER Zustand getragen und die
  // Liste auf `[]` gesetzt — die Flaeche behauptet danach nicht „No competitors
  // yet.", sondern zeigt den Fehler-Leerzustand im Panel und sperrt die Leiste.
  const load = useCallback(() => {
    setLoadError(null);
    setCompetitors(null);   // zurueck ins Skelett, auch beim Wiederholen
    fetch(`${API}/api/scrapers/competitors`)
      .then((res) => {
        // Z-1b: kein roher Statuscode. Die Flaeche unten nennt denselben Satz.
        if (!res.ok) throw new Error('Could not load competitors.');
        return res.json();
      })
      .then((data) => setCompetitors(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error('Error loading competitors:', err);
        setLoadError(err);
        setCompetitors([]);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);


  // Sort & filter, guarding against null. Die Suche bleibt CLIENTSEITIG und
  // wortgleich (Plan §2.4): der Endpunkt kennt keinen Suchparameter
  // (`backend/scrapers/routes.py`, `list_competitors`).
  const filtered = useMemo(() => {
    if (!competitors) return [];
    const q = suchbegriff.trim().toLowerCase();
    if (!q) return competitors;
    return competitors.filter((c) => String(c.name || '').toLowerCase().includes(q));
  }, [competitors, suchbegriff]);

  const sorted = useMemo(
    () => stableSort(filtered, getComparator(sortOrder, sortBy)),
    [filtered, sortOrder, sortBy],
  );

  const paged = useMemo(() => {
    const start = seite * ROWS_PER_PAGE;
    return sorted.slice(start, start + ROWS_PER_PAGE);
  }, [sorted, seite]);

  // N10.4: der Hook kennt die Zeilenzahl nicht — das Klemmen bleibt beim
  // Aufrufer. Eine Seite hinter dem Ende springt auf die letzte.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sorted.length / ROWS_PER_PAGE) - 1);
    if (seite > maxPage) set('page', maxPage + 1);
  }, [sorted.length, seite, set]);

  const stats = useMemo(() => {
    if (!competitors || competitors.length === 0) return null;
    const total = competitors.length;
    const totals = competitors.reduce(
      (acc, c) => {
        const scrapers = Number(c.scraperCount || 0);
        const products = Number(c.productCount || 0);
        const markets = Number(c.marketCount || 0);
        acc.targets += scrapers;
        acc.products += products;
        acc.markets += markets;
        if (scrapers === 0) acc.noTargets += 1;
        return acc;
      },
      { targets: 0, products: 0, markets: 0, noTargets: 0 }
    );
    // A four-bucket `donutItems` distribution used to be computed here and
    // returned in `stats`; nothing ever rendered it. Removed rather than wired
    // up: this payload carries no staleness field, so the donut would have
    // restated marketCount, which the tile below already shows.
    return {
      total,
      avgTargets: totals.targets / total,
      avgProducts: totals.products / total,
      avgMarkets: totals.markets / total,
      noTargets: totals.noTargets,
    };
  }, [competitors]);

  // Handlers
  // 🔴 W1 (Plan §2.2 Punkt 5): `unknownTotal` NUR im ungefilterten Zustand.
  // Der Endpunkt kappt nicht (146 Zeilen, §4.4a) — die Kappung ist hier
  // gegenstandslos und bleibt trotzdem richtig gebaut (`false`).
  // Im Fehler des ersten Aufbaus traegt der Zaehler NICHTS (`n = null`): eine 0
  // waere die Behauptung „null Wettbewerber", wo „konnte nicht laden" gilt.
  const zaehler = loadError
    ? { noun: 'competitors', n: null, m: null }
    : { noun: 'competitors', n: sorted.length, m: (competitors || []).length, unknownTotal: false };

  const sortLabel = (SORT_OPTIONS.find((o) => o.value === sortFeld) || SORT_OPTIONS[0]).label;

  const handleRequestSort = (property) => {
    const istAsc = sortBy === property && sortOrder === 'asc';
    set('sort', istAsc ? `-${property}` : property);
  };
  const handleDeleteClick = (id) => {
    setCompetitorToDelete(id);
    setConfirmOpen(true);
  };
  const executeDelete = () => {
    if (!competitorToDelete) return;
    fetch(`${API}/api/scrapers/competitors/${competitorToDelete}`, { method: 'DELETE' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to delete competitor');
        setCompetitors(prev => prev.filter(c => c.id !== competitorToDelete));
      })
      .catch(err => console.error('Delete error:', err))
      .finally(() => {
        setConfirmOpen(false);
        setCompetitorToDelete(null);
      });
  };

  // 🔴 R16 C1 (2026-10-06): der Kopf steht VOR allen Zweigen und wird in JEDEM Zustand
  // gerendert. Vorher kehrte der Ladezustand in ein Skelett zurueck, das nur graue Balken
  // trug — und die mobile Leiste schweigt seit Befund 5(i): im Laden stand GAR KEIN Titel.
  const kopf = (
    <Box data-testid="liste-kopf">
      <ViewHeader
        title="Competitors"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Competitors' },
        ]}
        actions={[
          {
            key: 'add',
            label: 'Add competitor',
            startIcon: <AddIcon fontSize="small" />,
            onClick: () => setModalOpen(true),
          },
        ]}
      />
    </Box>
  );

  // 🔴 R16 B1 (Nachbesserung 3): das Fenster haengt NICHT am geladenen Zweig.
  // `<CompetitorModal>` und der Loesch-`Dialog` standen bis hier im letzten
  // `return`; die zwei Zweige darueber kehren aber VORHER zurueck — „Add
  // competitor" trug damit im Fehler- und im Ladezustand einen Knopf, dessen
  // Fenster in diesem Zustand gar nicht montiert war. Genau das ist schlimmer
  // als der Zustand davor (da fehlte der Knopf), weil ein sichtbarer Knopf ohne
  // Wirkung eine Handlung verspricht, die nicht kommt.
  // Beide Fenster stehen jetzt neben `kopf` und werden in JEDEM Zweig
  // mitgerendert. Sie setzen nichts voraus, was nur im geladenen Zweig
  // existiert: `CompetitorModal` nimmt ausser `open`/`onClose`/`onSuccess`
  // nichts, der `Dialog` nur `confirmOpen`.
  const fenster = (
    <>
      <CompetitorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this competitor? This will also delete all
            associated scraper targets. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={executeDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (competitors === null) {
    // 🔴 Welle 2 PR 3b, Nachbesserung 2 (2026-10-06): `kopf` steht IM
    // `PageContainer`, genau wie geladen (`:247`). Die alte Fassung stellte ihn
    // daneben mit der Begruendung, ein Kopf ausserhalb trage „denselben Rand" —
    // das ist falsch, und es ist messbar: ein Kopf AUSSERHALB traegt
    // `px: PAGE_GUTTER` (12 px), ein Kopf INNERHALB traegt `px: 0`, und darunter
    // greift der `MuiContainer`-Override des Themes, der unter `sm`
    // `paddingLeft/Right: 16px` setzt (`theme.js`). Gemessen bei 390 px: Titel im
    // Laden x = 12, geladen x = 28 — ein waagerechter Sprung von 16 px genau beim
    // Eintreffen der Daten.
    return (
      <PageContainer width="full">
        {kopf}
        <CompetitorListSkeleton />
        {fenster}
      </PageContainer>
    );
  }

  // 🔴 N8.5 (G9b): der Fehler des ERSTEN Aufbaus sperrt die Bedienelemente —
  // er versteckt sie NICHT. Die Seite laedt nur einmal je Mount, ein Retry
  // rendert das Skelett; die Unterscheidung „spaeter gescheitertes Auffrischen"
  // beschreibt einen Zustand, den diese Seite nicht hat (Plan §2.2 Punkt 8).
  const sperre = Boolean(loadError);

  const leerGrund = loadError ? 'error' : ((competitors || []).length > 0 ? 'no-match' : 'no-data');

  // Der EINE Leerzustand, an ZWEI Montagepunkten (Desktop im Tabellenkoerper,
  // mobil im Kartenstapel) — eine Quelle, zwei Orte (Plan §2.2 Punkt 7).
  const leerzustand = (
    <EmptyState
      reason={leerGrund}
      {...(leerGrund === 'no-match'
        ? {
          query: suchbegriff.trim(),
          noun: 'competitor',
          onClearSearch: () => set('q', ''),
        }
        : {})}
      {...(leerGrund === 'no-data' ? { message: 'No competitors yet.' } : {})}
      {...(leerGrund === 'error' ? { message: 'Could not load competitors.' } : {})}
      action={
        leerGrund === 'error' ? (
          <Button variant="outlined" size="small" onClick={load}>Try again</Button>
        ) : leerGrund === 'no-data' ? (
          <Button variant="outlined" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => setModalOpen(true)}>
            Add competitor
          </Button>
        ) : undefined
      }
    />
  );

  // 🔴 N9.9: der Link ist der Tastaturweg (⌘-/Mittelklick, zugänglicher Name);
  // die Zeile oeffnet per Klick. Ohne `stopPropagation` navigierte der Klick
  // ZWEIMAL (Link + Zeile). Die Zeile selbst bekommt KEIN `tabIndex`/`role` —
  // sonst zwei Tab-Stopps und ein kaputter `rowgroup` (Hausnotiz
  // `RMACasesDashboardBody.jsx`).
  const titelLink = (comp) => (
    <MuiLink
      component={RouterLink}
      to={`/competitors/${comp.id}`}
      underline="hover"
      color="inherit"
      // 🔴 R16 W1 (Nachbesserung 4, 2026-10-22): der gekuerzte Name steht
      // vollstaendig im `title` — Hausform der gekuerzten Texte
      // (`ProductsTable.jsx:760`). Am Telefon folgenlos, am Tablet nicht.
      title={comp.name}
      sx={KARTE_TITEL_SX}
      onClick={(e) => e.stopPropagation()}
    >
      {/* 🔴 R16 B1: die Zwei-Zeilen-Klemme sitzt am INNEREN `<span>`, nicht am
          `Link` — sonst schneidet ihr `overflow: hidden` den 44-px-Boden
          (`::after`) auf die Textzeile ab (Begruendung an `KARTE_TITEL_SX`).
          Die Marke `karte-titel-klemme-` ist die Kopplung an P15: die Probe
          misst Klemme und Zeilenzahl HIER und den Boden am `Link`. */}
      <Box component="span" data-testid={`karte-titel-klemme-${comp.id}`} sx={TITEL_KLEMME_SX}>
        {comp.name}
      </Box>
    </MuiLink>
  );

  // B9: der Chip haengt an `scraperCount === 0` — sonst gibt es KEINEN Slot.
  // 🔴 W8 (Nachbesserung 1, R15): `null` ist NICHT `0`. Nach dem Anlegen fehlt
  // `scraperCount` in der POST-Antwort (`{ marketCount: 0, ...newComp }`), und
  // `zahl()` schrieb daneben „— targets". Mit `?? 0` behauptete die Pille dann
  // „null Ziele" ueber einen UNBEKANNTEN Wert — genau die Regel-22-Nr.-3-Sorte
  // („unbekannt ist nie 0"), nur als Etikett statt als Zahl. Vorher stand in der
  // Zelle eine `0`: falsch, aber in sich stimmig. Jetzt traegt die Zeile weder
  // Zahl noch Pille, bis der naechste Abruf den Wert kennt.
  const chips = (comp) => (
    comp.scraperCount === 0 ? <Pill tone="warn" nowrap>No targets</Pill> : null
  );

  const meta = (comp) => (
    <Typography variant="caption" color="text.secondary">
      {zahl(comp.scraperCount)} targets · {zahl(comp.productCount)} products · {zahl(comp.marketCount)} markets
    </Typography>
  );

  return (
    <PageContainer width="full">
      {kopf}

      {stats && (
        <Box data-testid="liste-kpi">
          <KpiRow sx={{ mb: 3 }}>
            <KpiCell>
              <StatTile
                label="Total competitors"
                value={stats.total}
                labelTooltip="Distinct competitor accounts configured in the scraper service."
              />
            </KpiCell>
            <KpiCell>
              <StatTile
                label="Avg targets per competitor"
                value={formatNumber(stats.avgTargets, { digits: 1 })}
                labelTooltip="Scraper targets divided by total competitors."
              />
            </KpiCell>
            <KpiCell>
              <StatTile
                label="Avg products per competitor"
                value={formatNumber(stats.avgProducts, { digits: 1 })}
                labelTooltip="Number of unique SKUs each competitor tracks on average."
              />
            </KpiCell>
            <KpiCell>
              <StatTile
                label="Avg markets per competitor"
                value={formatNumber(stats.avgMarkets, { digits: 1 })}
                hint={`${stats.noTargets} competitors have no targets yet`}
                labelTooltip="Distinct market codes covered per competitor."
              />
            </KpiCell>
          </KpiRow>
        </Box>
      )}

      <Panel data-testid="liste-panel" sx={{ mb: SP.md }}>
          {/* N8.5: der GRUND der Sperre steht sichtbar DIREKT ueber der Leiste
              und sagt etwas anderes als der Leerzustand — der `EmptyState` nennt,
              WAS fehlschlug, dieser Satz nennt, WARUM die Bedienelemente gesperrt
              sind. */}
          {/* 🔴 R16 B1 (Nachbesserung 1): MUI setzt an einem `Alert` ohne `role`
              ein ASSERTIVES `role="alert"` — und der Fehler-Leerzustand darunter
              ist ebenfalls `role="alert"` (`EmptyState.jsx`, `reason === 'error'`).
              Dann sagt der Sperrhinweis (die FOLGE) assertiv vor der Ursache an.
              `role="status"` stuft ihn ab, der Wortlaut bleibt. Hausform
              `ScraperList.jsx:2132-2146`.
              🔴 R16 W6 (Nachbesserung 1): der zweite Satz „Use Try again below."
              ist entfallen — er zeigte ins Leere, sobald `EmptyState` im
              BESTAETIGTEN Ausfall schweigt (Z-1a). Dann stuende hier ein Verweis
              auf einen Knopf, den es nicht gibt. Den Ausfall hier zu LESEN waere
              der Renderzeit-Entscheid, den `EmptyState.jsx` als Anti-Muster
              beschreibt (kein Abonnement, greift nur in EINER Reihenfolge);
              `showDuringOutage` zu setzen machte diese Seite zur Ausnahme von
              Z-1a und meldete den Ausfall doppelt, obwohl der Banner unter dem
              Panel sichtbar bleibt. Im gewoehnlichen Fehler steht „Try again"
              direkt darunter, im bestaetigten Ausfall traegt der Banner „Reload". */}
          {sperre && (
            <Alert
              severity="warning"
              role="status"
              data-testid="erster-aufbau-gesperrt"
              sx={{ mb: SP.card }}
            >
              Search and filters are locked until the list loads.
            </Alert>
          )}

          {/* N10.4: ein verworfener URL-Wert wird EINMAL gemeldet, nicht
              still verschluckt. Hausform (Plan §3.2/W11) — der Wortlaut ist
              englisch wie die Seite, die Marke ist die Hausmarke. */}
          {query.ignoredValues.length > 0 && (
            <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mb: SP.card }}>
              {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
            </Alert>
          )}

          <ListToolbar
            shortcut="/"
            search={{
              value: suchbegriff,
              onChange: (v) => set('q', v),
              onCommit: (v) => set('q', v),
              placeholder: 'Search competitors…',
              // P5: der zugaengliche Name beginnt mit „Search".
              label: 'Search competitors',
              // P5/P34: die Rolle, nicht nur der Platzhalter.
              inputRole: 'searchbox',
              disabled: sperre,
              // N2.9: die Suche fuellt den Rest von Z1.
              sx: { flex: 1, minWidth: 0 },
            }}
            count={zaehler}
            // 🔴 BS6 NUR unter `sm` (Plan §2.1): der Desktop traegt die
            // Sortierung im Tabellenkopf; ein zweiter Sortierweg dort waere
            // eine Doppelquelle. `criteria: []` — diese Liste hat keine Facette.
            moreFilters={isMobile ? {
              disabled: sperre,
              criteria: [],
              sort: {
                value: sortFeld,
                onChange: (v) => set('sort', v),
                options: SORT_OPTIONS,
              },
              resultCount: sperre ? undefined : sorted.length,
              // Sortieren ist kein Filter (N4.6).
              activeCount: 0,
              // 🔴 B4: ohne `onClearAll` rendert `FilterSheet` KEIN „Clear all".
              onClearAll: clearAll,
              label: 'Filters',
              title: 'Filters',
            } : undefined}
            query={{ chips: query.chips, clearAll }}
          >
            {/* Mobil: der EINE Sortiersatz unter der Leiste. Der Desktop traegt
                die Ordnung im Tabellenkopf (`aria-sort`) — ein zweiter Weg auf
                derselben Breite waere einer zu viel (Plan §2.2 Punkt 9). */}
            {isMobile && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="sortiert-nach"
                sx={{ display: 'block', mt: SP.tight }}
              >
                {`Sorted by ${sortLabel}.`}
              </Typography>
            )}

            {isMobile ? (
              /* ─── Mobile: card list ─── */
              <Stack data-testid="liste-karte" spacing={0} sx={{ mt: SP.tight }}>
                {sorted.length === 0 && leerzustand}
                {paged.map((comp) => (
                  // 🔴 B1: B3 hat keinen sichtbaren `id`-Slot (die Kennung ist der
                  // Name, N11.2). Der Karten-WRAPPER traegt deshalb die Marke
                  // `karte-kennung-competitor` — Anker fuer P2/P20/P15, ohne ein
                  // neues Feld zu erfinden (N11.3: „nichts kommt hinzu").
                  <Box key={comp.id} data-testid="karte-kennung-competitor">
                    <MobileRecordCard
                      title={titelLink(comp)}
                      chips={chips(comp)}
                      meta={meta(comp)}
                      // 🔴 W6: `RowActionsMenu` stoppt nur `onClick`; der
                      // `actions`-Slot der Karte stoppt gar nichts. Die Hülle
                      // stoppt beides, damit der Kartenklick nicht mitfeuert.
                      actions={(
                        <Box onPointerDown={(e) => e.stopPropagation()}>
                          <RowActionsMenu
                            label={`Actions for ${comp.name}`}
                            actions={[
                              { key: 'view', label: 'View', onClick: () => navigate(`/competitors/${comp.id}`) },
                              { key: 'delete', label: 'Delete', danger: true, onClick: () => handleDeleteClick(comp.id) },
                            ]}
                          />
                        </Box>
                      )}
                      slotOrder="chips-first"
                      fieldTestIds={{
                        title: 'karte-titel-competitor',
                        meta: 'karte-meta-competitor',
                        chips: 'karte-chips-competitor',
                        actions: 'karte-aktionen-competitor',
                      }}
                      onClick={() => navigate(`/competitors/${comp.id}`)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                ))}
              </Stack>
            ) : (
              /* ─── Desktop: table ─── */
              <Box data-testid="liste-tabelle" sx={{ width: '100%', overflowX: 'auto' }}>
                {/* Hauskopf statt handgerolltem `thead`-sx (Plan §2.4): kein
                    rohes `fontSize`, keine graue Flaeche von Hand. */}
                <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 760 }}>
                  <TableHead>
                    <TableRow>
                      {SORT_FELDER.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          align={headCell.numeric ? 'right' : headCell.id === 'actions' ? 'center' : 'left'}
                          // 🔴 B7: `aria-sort` kommt aus `TableCell.sortDirection`;
                          // `TableSortLabel` allein stempelt es NICHT.
                          sortDirection={headCell.sortierbar !== false && sortBy === headCell.id ? sortOrder : false}
                          title={headCell.label}
                          sx={{ width: headCell.width }}
                        >
                          {headCell.sortierbar === false ? (
                            headCell.label
                          ) : (
                            // 🔴 R16 W5 (Nachbesserung 1): im gesperrten Zustand
                            // traegt der Kopf `disabled`. Vorher stand der
                            // Sortierpfeil aktiv ueber der Meldung „Search and
                            // filters are locked until the list loads", und ein
                            // Klick aenderte nur die URL — sichtbar passierte
                            // nichts. Die Ansage und die Flaeche widersprachen
                            // sich. `aria-sort` bleibt am `TableCell` (B7) und
                            // ist auch gesperrt die wahre Ordnung.
                            <TableSortLabel
                              active={sortBy === headCell.id}
                              direction={sortBy === headCell.id ? sortOrder : 'asc'}
                              disabled={sperre}
                              // 🔴 R16 W4 (Nachbesserung 4, 2026-10-22): der
                              // gesperrte Kopf sieht jetzt auch gesperrt aus.
                              // MUI faerbt `disabled` an `TableSortLabel` NICHT
                              // von selbst — im Foto trug „COMPETITOR ↑" im
                              // Fehlerzustand dieselbe Farbe und Strichstaerke wie
                              // im geladenen, die Ansage (`aria-disabled`) und die
                              // Flaeche widersprachen sich. Die Farbe kommt aus
                              // dem Theme (`text.disabled`), kein Literal
                              // (Regel 22). `&.Mui-disabled` ist ZWEI Klassen
                              // (0,2,0) und schlaegt die eine Klasse der
                              // Grundfarbe; bei Gleichstand mit
                              // `.Mui-active` entscheidet die spaetere
                              // Einfuegung — die `sx`-Regel (Hausform
                              // `FilterSheet.jsx:532-543`).
                              sx={{ '&.Mui-disabled': { color: 'text.disabled' } }}
                              onClick={() => handleRequestSort(headCell.id)}
                            >
                              {headCell.label}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((comp) => (
                      <TableRow
                        key={comp.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/competitors/${comp.id}`)}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                            {titelLink(comp)}
                            {chips(comp)}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{zahl(comp.scraperCount)}</TableCell>
                        <TableCell align="right">{zahl(comp.productCount)}</TableCell>
                        <TableCell align="right">{zahl(comp.marketCount)}</TableCell>
                        <TableCell align="center">
                          <RowActionsMenu
                            label={`Actions for ${comp.name}`}
                            actions={[
                              { key: 'view', label: 'View', onClick: () => navigate(`/competitors/${comp.id}`) },
                              { key: 'delete', label: 'Delete', danger: true, onClick: () => handleDeleteClick(comp.id) },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {sorted.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={SORT_FELDER.length} sx={{ border: 0 }}>
                          {leerzustand}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* 🔴 B8 (N8.7): die Marke steht NUR mit Inhalt. `TableFooterSimple`
                gibt nur bei `count === 0` `null` zurueck; ohne `leftSlot` und
                ohne Seitenwechsel bliebe ein leerer 44-px-Streifen stehen.
                Geblaettert wird erst ueber 50 Zeilen. */}
            {sorted.length > ROWS_PER_PAGE && (
              <Box data-testid="liste-fuss">
                <TableFooterSimple
                  count={sorted.length}
                  page={seite}
                  onPageChange={(p) => set('page', p + 1)}
                  rowsPerPage={ROWS_PER_PAGE}
                  // 🔴 BS17: mobil steht die Leiste im FLUSS statt zu schweben —
                  // die schwebende Fassung deckt die Kartenliste zu.
                  stickyOnMobile={false}
                />
              </Box>
            )}
          </ListToolbar>
        </Panel>

      {/* B1: die zwei Fenster stehen neben `kopf` und werden in jedem Zweig
          mitgerendert — hier genau wie im Fehler- und im Ladezweig. */}
      {fenster}
    </PageContainer>
  );
}
